import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Bell, CheckCheck, MessageCircle, Star, ShieldCheck } from 'lucide-react';
import { apiBaseUrl } from '../config/runtime';
import { localizeNotification } from '../i18n/systemMessages';
import { getToken } from '../utils/auth';

interface Notification {
  id: number;
  type: string;
  title: string;
  body: string;
  link: string | null;
  is_read: number;
  created_at: string;
}

const typeIcon: Record<string, React.ReactNode> = {
  review_received: <Star size={16} color="#f59e0b" />,
  review_request:  <Star size={16} color="#f59e0b" />,
  message:         <MessageCircle size={16} color="#a78bfa" />,
  trade_complete:  <CheckCheck size={16} color="#34d399" />,
  insurance:       <ShieldCheck size={16} color="#34d399" />,
  default:         <Bell size={16} color="rgba(255,255,255,0.35)" />,
};

const NotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('[NotificationsPage] 알림 목록 로드');
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const token = getToken();
      const res = await axios.get(apiBaseUrl('/notifications'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setNotifications(res.data.data);
        setUnreadCount(res.data.unreadCount);
        // 오프라인 대비 캐시 (최근 50개)
        try {
          localStorage.setItem('cached_notifications', JSON.stringify({
            data: res.data.data.slice(0, 50),
            unreadCount: res.data.unreadCount,
            cachedAt: Date.now(),
          }));
        } catch { /* 저장 실패는 무시 */ }
      }
    } catch (err) {
      console.error('[NotificationsPage] 알림 로드 실패 — 오프라인 캐시 사용 시도:', err);
      // 오프라인/서버 장애 시 마지막 캐시 표시
      try {
        const cached = localStorage.getItem('cached_notifications');
        if (cached) {
          const { data, unreadCount } = JSON.parse(cached);
          setNotifications(data);
          setUnreadCount(unreadCount);
          console.log('[NotificationsPage] 캐시된 알림', data.length, '건 표시 (오프라인)');
        }
      } catch { /* 캐시 파싱 실패는 무시 */ }
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id: number, link: string | null) => {
    console.log(`[NotificationsPage] 알림 읽음 처리: ${id}`);
    try {
      const token = getToken();
      await axios.put(apiBaseUrl(`/notifications/${id}/read`), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
    if (link) navigate(link);
  };

  const markAllRead = async () => {
    console.log('[NotificationsPage] 전체 읽음 처리');
    try {
      const token = getToken();
      await axios.put(apiBaseUrl('/notifications/read-all'), {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
    if (diff < 60) return t('notifications.justNow');
    if (diff < 3600) return t('notifications.minutesAgo', { n: Math.floor(diff / 60) });
    if (diff < 86400) return t('notifications.hoursAgo', { n: Math.floor(diff / 3600) });
    if (diff < 604800) return t('notifications.daysAgo', { n: Math.floor(diff / 86400) });
    return d.toLocaleDateString(i18n.language === 'ko' ? 'ko-KR' : 'en-US');
  };

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 20px', minHeight: '100vh', background: '#000' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', marginBottom: 4 }}>
            {t('notifications.title')}
          </h1>
          {unreadCount > 0 && (
            <span style={{ fontSize: 13, color: '#a78bfa', fontWeight: 600 }}>
              {t('notifications.unreadN', { n: unreadCount })}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '7px 14px',
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, fontSize: 13,
              fontWeight: 500, cursor: 'pointer', color: 'rgba(255,255,255,0.6)',
              fontFamily: 'inherit',
            }}
          >
            <CheckCheck size={14} />{t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {/* 알림 목록 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px', color: 'rgba(255,255,255,0.25)', fontSize: 14 }}>
          {t('common.loading')}
        </div>
      ) : notifications.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '64px 20px',
          border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 14,
          background: '#0d0d0d',
        }}>
          <Bell size={40} color="rgba(255,255,255,0.15)" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.35)', marginBottom: 4 }}>{t('notifications.empty')}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.2)' }}>{t('notifications.emptyDesc')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {notifications.map(n => (
            <div
              key={n.id}
              onClick={() => markRead(n.id, n.link)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                padding: '14px 16px',
                background: n.is_read ? '#0d0d0d' : 'rgba(124,58,237,0.08)',
                border: `1px solid ${n.is_read ? 'rgba(255,255,255,0.06)' : 'rgba(124,58,237,0.2)'}`,
                borderRadius: 12,
                cursor: n.link ? 'pointer' : 'default',
                transition: 'all 0.15s',
              }}
              onMouseOver={e => { if (n.link) e.currentTarget.style.background = n.is_read ? '#141414' : 'rgba(124,58,237,0.12)'; }}
              onMouseOut={e => { e.currentTarget.style.background = n.is_read ? '#0d0d0d' : 'rgba(124,58,237,0.08)'; }}
            >
              {/* 아이콘 */}
              <div style={{
                width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                background: n.is_read ? 'rgba(255,255,255,0.05)' : 'rgba(124,58,237,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {typeIcon[n.type] || typeIcon.default}
              </div>

              {/* 내용 */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 3 }}>
                  <span style={{ fontSize: 14, fontWeight: n.is_read ? 400 : 700, color: n.is_read ? 'rgba(255,255,255,0.6)' : '#fff' }}>
                    {localizeNotification(n).title}
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', flexShrink: 0, marginLeft: 8 }}>
                    {formatDate(n.created_at)}
                  </span>
                </div>
                {n.body && (
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {localizeNotification(n).body}
                  </div>
                )}
              </div>

              {/* 읽음 표시 */}
              {!n.is_read && (
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#a78bfa', flexShrink: 0, marginTop: 4 }} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
